import { useEffect, useMemo, useRef } from "react";
import { X } from "lucide-react";

import { Annotation } from "@/types/annotations";
import { depthToRedBlue, imagePxToDepthPx, type DepthFrame } from "@/lib/measurement/depth3d";
import { buildMeasurementLineDrawSpecs } from "@/utils/annotationMeasurementUtils";
import type { AnnotationViewport } from "../AnnotationViewerBase/helpers";

import type { MeasurementDragDraft } from "./SegmentationViewer";
import { computeDepthDebugTransform } from "./depthDebugUtils";

const PANEL_WIDTH = 256;
const DEPTH_OVERLAY_COLORS = ["#FDE047", "#4ADE80", "#FFFFFF"] as const;
const DEPTH_OVERLAY_HALO_COLOR = "#111827";

type DepthDebugViewProps = {
  depthFrame: DepthFrame;
  annotation?: Annotation | null;
  viewport: AnnotationViewport;
  liveDraft?: MeasurementDragDraft | null;
  onClose: () => void;
};

/**
 * Debug-only overlay that mirrors the depth map (right-hand panel) with the
 * measurement endpoints/lines mapped through `imagePxToDepthPx` — the exact
 * pixels the recompute samples. As an endpoint is dragged, its mirrored point
 * moves here too, so you can verify the depth map is aligned with the image and
 * the rotation mapping is correct.
 */
export const DepthDebugView = ({ depthFrame, annotation, viewport, liveDraft, onClose }: DepthDebugViewProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const depthWidth = depthFrame.depth.width;
  const depthHeight = depthFrame.depth.height;
  const displayHeight = Math.round((PANEL_WIDTH * depthHeight) / depthWidth);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { data, width, height } = depthToRedBlue(depthFrame.meters, depthWidth, depthHeight);
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d")?.putImageData(new ImageData(data, width, height), 0, 0);
  }, [depthFrame, depthWidth, depthHeight]);

  const transform = useMemo(
    () => computeDepthDebugTransform(viewport, depthFrame.image, depthFrame.depth, PANEL_WIDTH, displayHeight),
    [depthFrame.depth, depthFrame.image, displayHeight, viewport]
  );

  // Map only the selected annotation's endpoints into depth-pixel coords.
  const overlays = useMemo(() => {
    if (!annotation || annotation.userVisibility?.isVisible === false) return [];
    const toDepth = (pt: { x: number; y: number }) => imagePxToDepthPx(pt, depthFrame.image, depthFrame.depth);
    const effective = (name: string | undefined, stored: { x: number; y: number }) =>
      liveDraft && liveDraft.annotationId === annotation.id && liveDraft.keypointName === name
        ? { x: liveDraft.x, y: liveDraft.y }
        : stored;

    return buildMeasurementLineDrawSpecs(annotation.keypoints).map((axis, index) => ({
      color: DEPTH_OVERLAY_COLORS[index % DEPTH_OVERLAY_COLORS.length],
      a: toDepth(effective(axis.start.name, axis.start)),
      b: toDepth(effective(axis.end.name, axis.end)),
    }));
  }, [annotation, depthFrame, liveDraft]);

  return (
    <div className="absolute right-2 top-2 z-10 rounded-md border border-gray-300 bg-white/95 shadow-lg">
      <div className="flex items-center justify-between px-2 py-1 text-xs font-semibold text-gray-700">
        <span>
          Depth Map View ({depthWidth}×{depthHeight}
          {depthFrame.image.rotationToDepthFrameDegrees ? `, ${depthFrame.image.rotationToDepthFrameDegrees}°` : ""})
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-2 rounded p-0.5 hover:bg-gray-200"
          aria-label="Close depth map view">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div
        className="relative overflow-hidden"
        style={{ width: PANEL_WIDTH, height: displayHeight }}>
        <div
          data-testid="depth-debug-content"
          className="absolute left-0 top-0"
          style={{
            width: PANEL_WIDTH,
            height: displayHeight,
            transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
            transformOrigin: "0 0",
          }}>
          <canvas
            ref={canvasRef}
            style={{ width: PANEL_WIDTH, height: displayHeight, imageRendering: "pixelated", display: "block" }}
          />
          <svg
            className="absolute inset-0"
            width={PANEL_WIDTH}
            height={displayHeight}
            viewBox={`0 0 ${depthWidth} ${depthHeight}`}
            preserveAspectRatio="none">
            {overlays.map((overlay, index) => (
              <g key={index}>
                <line
                  data-depth-overlay-line="halo"
                  x1={overlay.a.u}
                  y1={overlay.a.v}
                  x2={overlay.b.u}
                  y2={overlay.b.v}
                  stroke={DEPTH_OVERLAY_HALO_COLOR}
                  strokeWidth={3.5}
                  vectorEffect="non-scaling-stroke"
                />
                <line
                  data-depth-overlay-line="color"
                  x1={overlay.a.u}
                  y1={overlay.a.v}
                  x2={overlay.b.u}
                  y2={overlay.b.v}
                  stroke={overlay.color}
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                />
                <circle
                  cx={overlay.a.u}
                  cy={overlay.a.v}
                  r={2}
                  fill={overlay.color}
                  stroke={DEPTH_OVERLAY_HALO_COLOR}
                  strokeWidth={0.75}
                  vectorEffect="non-scaling-stroke"
                />
                <circle
                  cx={overlay.b.u}
                  cy={overlay.b.v}
                  r={2}
                  fill={overlay.color}
                  stroke={DEPTH_OVERLAY_HALO_COLOR}
                  strokeWidth={0.75}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
};
